import { getUniqueReactions } from '../reactions/reactionUtil';
import type {
    TaskItemAssignment,
    TaskItemChipAttrs,
    TaskItemDueDate,
} from '../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type {
    ElementAssignmentChipDescriptor,
    ElementAssignmentPlaceholderChipDescriptor,
    ElementChipDescriptor,
    ElementDueDateChipDescriptor,
} from './elementChipsTypes';
import type { LabelMap } from '../../../common/labels/labelTypes';

/**
 * Chip-relevant task item node attrs. `checked` comes from the base task item
 * schema (not the chip attrs) and is used for the due-date chip's complete state.
 */
export type TaskItemChipSource = TaskItemChipAttrs & { checked?: boolean };

/** Updates a subset of the owning task node's attrs (the node view's `updateAttributes`). */
export type UpdateTaskItemAttrs = (attrs: Partial<TaskItemChipAttrs>) => void;

/**
 * The container ID that keys a chip's selection/editing/popover state. For task nodes this is the
 * stable `taskId`. It can briefly be `null` before the id plugin stamps it, in
 * which case the chip still renders but isn't keyed (selection wiring is Step 4).
 */
const getContainerId = (taskId: string | null): string => taskId ?? '';

/**
 * Builds a task item's due-date chip descriptor. Also used by the due-date
 * toolbar tool to select the chip it has just toggled on, so the descriptor
 * shapes stay in lockstep.
 */
export const buildTaskItemDueDateChipDescriptor = ({
    taskId,
    dueDate = null,
    checked = false,
}: {
    taskId: string | null;
    dueDate?: TaskItemDueDate | null;
    checked?: boolean;
}): ElementDueDateChipDescriptor => ({
    type: 'due-date',
    id: 'due-date',
    containerId: getContainerId(taskId),
    dueReminder: dueDate?.reminder,
    dueTimestamp: dueDate?.timestamp ?? null,
    hasDueDateTime: dueDate?.hasTime ?? false,
    isComplete: Boolean(checked),
});

const getTaskDueDateChipDescriptor = ({
    taskId,
    dueDate,
    showDueDate,
    checked,
}: TaskItemChipSource): ElementChipDescriptor[] => {
    // Mirror the element `showDueDate` gate: the chip renders as the "Set due
    // date" placeholder (null timestamp) until a date is chosen. A set date also
    // keeps the chip visible on its own, so pre-`showDueDate` documents still work.
    if (!showDueDate && !dueDate) return [];

    return [buildTaskItemDueDateChipDescriptor({ taskId, dueDate, checked })];
};

/**
 * Builds a task item's empty "Assign to" placeholder chip descriptor. Also used
 * by the assignment toolbar tool to select the chip it has just toggled on, so
 * the descriptor shapes stay in lockstep.
 */
export const buildTaskItemAssignmentPlaceholderChipDescriptor = (
    taskId: string | null,
): ElementAssignmentPlaceholderChipDescriptor => ({
    type: 'assignment-placeholder',
    // A task item has a single assignee slot, so the placeholder and the assigned
    // chip share one stable id: chip selection then survives the placeholder ->
    // assigned (and re-assignment) transitions without the chip remounting.
    id: 'assignment',
    containerId: getContainerId(taskId),
});

/**
 * Builds a task item's assignment chip descriptor for one assignee. Also used
 * by the assignment shortcut to select the chip of an already-assigned task, so the
 * descriptor shapes stay in lockstep.
 */
export const buildTaskItemAssignmentChipDescriptor = (
    taskId: string | null,
    { assigneeId, assigneeName }: TaskItemAssignment,
): ElementAssignmentChipDescriptor => ({
    type: 'assignment',
    // Stable across re-assignment (see the placeholder builder) — a task item has a
    // single assignee slot, so the id does not include the assignee.
    id: 'assignment',
    containerId: getContainerId(taskId),
    assigneeId,
    assigneeName,
});

const getTaskAssignmentChipDescriptors = ({
    taskId,
    assignments,
    showAssignments,
}: TaskItemChipSource): ElementChipDescriptor[] => {
    // Mirror the element `showAssignments` gate: when off, no assignment chips at
    // all; when on with no assignees, the empty "Assign to" placeholder.
    if (!showAssignments) return [];

    if (assignments.length === 0) {
        return [buildTaskItemAssignmentPlaceholderChipDescriptor(taskId)];
    }

    return assignments.reduce<ElementChipDescriptor[]>((descriptors, assignment) => {
        if (!assignment.assigneeId || !assignment.assigneeName) return descriptors;

        descriptors.push(buildTaskItemAssignmentChipDescriptor(taskId, assignment));

        return descriptors;
    }, []);
};

/** Resolves task-item label display from hydrated labels, retaining the node cache only before hydration. */
const getTaskLabelChipDescriptors = ({ labels }: TaskItemChipSource, labelsById?: LabelMap): ElementChipDescriptor[] =>
    labels.reduce<ElementChipDescriptor[]>((descriptors, { labelId, name, colour }) => {
        if (!labelId) return descriptors;

        const label = labelsById?.[labelId];
        if (labelsById && !label) return descriptors;

        descriptors.push({
            type: 'label',
            id: `label-${labelId}`,
            label: label || { _id: labelId, name, color: colour },
        });

        return descriptors;
    }, []);

const getTaskReactionChipDescriptors = ({ reactions }: TaskItemChipSource): ElementChipDescriptor[] => {
    const uniqueReactions: {
        emojiCode: string;
        userIds: string[];
    }[] = Object.values(getUniqueReactions(reactions));

    return uniqueReactions.reduce<ElementChipDescriptor[]>((descriptors, reaction) => {
        if (!reaction.emojiCode) return descriptors;

        descriptors.push({
            type: 'reaction',
            id: `reaction-${reaction.emojiCode}`,
            emojiCode: reaction.emojiCode,
            userIds: reaction.userIds,
        });

        return descriptors;
    }, []);
};

/**
 * Gets the flat list of visible chip descriptors for a task item node's attrs.
 *
 * Parallels `getElementChipDescriptors` (`elementChipUtils.ts`) and produces the
 * same `ElementChipDescriptor` shapes from node attrs, so the task list editor
 * reuses the existing descriptor types and chip views unchanged.
 */
export const getTaskItemChipDescriptors = (
    attrs: TaskItemChipSource,
    labelsById?: LabelMap,
): ElementChipDescriptor[] => [
    ...getTaskDueDateChipDescriptor(attrs),
    ...getTaskAssignmentChipDescriptors(attrs),
    ...getTaskLabelChipDescriptors(attrs, labelsById),
    ...getTaskReactionChipDescriptors(attrs),
];
